import React from 'react';
import { FastField, ErrorMessage, useFormikContext } from 'formik';
import { Box, Grid, TextField, Typography } from '@material-ui/core';
import { makeStyles, createStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) =>
  createStyles({
    profileImageContainer: {
      flexGrow: 0,
      flexShrink: 0,
      flexBasis: '200px',
      textAlign: 'center',
    },
    avatarContainer: {
      border: '1px solid #d8d7d7',
      padding: theme.spacing(1),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
      backgroundColor: '#f9f9f9',
    },
    profileImage: {
      width: '180px',
      height: '180px',
      borderRadius: '50%',
      objectFit: 'cover',
      border: '2px solid #ddd',
      [theme.breakpoints.down('xs')]: {
        width: '150px',
        height: '150px',
      },
    },
    noAvatarPlaceholder: {
      width: '180px',
      height: '180px',
      borderRadius: '50%',
      backgroundColor: '#e0e0e0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#999',
      fontSize: '14px',
      textAlign: 'center',
      border: '2px dashed #ccc',
      [theme.breakpoints.down('xs')]: {
        width: '150px',
        height: '150px',
      },
    },
    sectionTitle: {
      fontWeight: 'bold',
      textTransform: 'uppercase',
    },
    avatarNote: {
      marginTop: theme.spacing(1),
      fontSize: '12px',
      color: '#666',
      textAlign: 'center',
    },
  })
);

function BasicInfo() {
  const classes = useStyles();
  const { values } = useFormikContext();
  
  // Lấy avatar từ form values hoặc base64 data
  const avatarSrc = 
    (values.basicInfo?.avatar ? `${process.env.REACT_APP_DOMAIN}/api/v1${values.basicInfo.avatar}` : null);

  return (
    <>
      <Box pt={2}>
        <Box className={classes.profileImageContainer} py={2}>
          <Box className={classes.avatarContainer}>
            {avatarSrc ? (
              <img 
                alt='Ảnh hồ sơ' 
                src={avatarSrc} 
                className={classes.profileImage} 
              />
            ) : (
              <div className={classes.noAvatarPlaceholder}>
                Chưa có ảnh đại diện
              </div>
            )}
            <div className={classes.avatarNote}>
              Để thay đổi ảnh đại diện, vui lòng sử dụng chức năng "Sửa avatar" bên trái
            </div>
          </Box>
        </Box>
        <Box>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant='h6' className={classes.sectionTitle}>
                Thông tin cơ bản
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <FastField name='basicInfo.name'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label='Tên'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.name' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item md={4} sm={7} xs={6}>
              <FastField name='basicInfo.birth'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label='Ngày, tháng, năm sinh'
                      variant='outlined'
                      size='small'
                      type='date'
                      helperText={<ErrorMessage name='basicInfo.birth' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item md={8} sm={5} xs={6}>
              <FastField name='basicInfo.nationality'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label='Quốc tịch'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.nationality' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name='basicInfo.degree'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label='Học hàm/học vị cao nhất'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.degree' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name='basicInfo.unit'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label='Đơn vị công tác'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.unit' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name='basicInfo.position'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label='Vai trò'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.position' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={12}>
              <FastField name='basicInfo.address'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      fullWidth
                      label='Địa chỉ'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.address' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={6}>
              <FastField name='basicInfo.email'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      type='email'
                      fullWidth
                      label='Email'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.email' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
            <Grid item xs={6}>
              <FastField name='basicInfo.phone'>
                {(fieldProps) => {
                  return (
                    <TextField
                      {...fieldProps.field}
                      type='tel'
                      fullWidth
                      label='Số điện thoại'
                      variant='outlined'
                      size='small'
                      helperText={<ErrorMessage name='basicInfo.phone' />}
                      FormHelperTextProps={{ error: true }}
                      InputLabelProps={{ shrink: true }}
                    />
                  );
                }}
              </FastField>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}

export default BasicInfo;
